import { useEffect, useState } from "react";
import { Plus, Trash2, X, Loader2, Send, AlertCircle, CalendarDays } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { Card, ClinicaLabel, PageTitle, formatDate } from "../ui";
import {
  listarMedicos, listarDisponibilidades, criarDisponibilidade, apagarDisponibilidade,
  listarBloqueios, criarBloqueio, apagarBloqueio, notificarBloqueio,
} from "../../lib/adminP1Api";
import { useAuth } from "../AuthContext";

const DIAS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export default function AdminDisponibilidadePage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [medicos, setMedicos] = useState([]);
  const [disps, setDisps] = useState([]);
  const [bloqueios, setBloqueios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDisp, setOpenDisp] = useState(false);
  const [openBloq, setOpenBloq] = useState(false);
  const [formDisp, setFormDisp] = useState({ medico_id: "", clinica_slug: "ribeira-brava", dia_semana: 0, hora_inicio: "09:00", hora_fim: "13:00", intervalo_slot_minutos: 30 });
  const [formBloq, setFormBloq] = useState({ medico_id: "", clinica_slug: "ribeira-brava", data_inicio: "", data_fim: "", motivo: "" });
  const [bloqResult, setBloqResult] = useState(null); // {bloqueio, afetadas}
  const [saving, setSaving] = useState(false);

  const carregar = async () => {
    setLoading(true);
    const [m, d, b] = await Promise.all([listarMedicos(), listarDisponibilidades(), listarBloqueios()]);
    setMedicos(m); setDisps(d); setBloqueios(b);
    setLoading(false);
  };
  useEffect(() => { carregar(); }, []);

  const inputCls = "w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 px-3 py-2 text-sm focus:border-[#1E5F6E] focus:outline-none";

  const dispsPorMedico = (mid) => disps.filter((d) => d.medico_id === mid);

  const submitDisp = async (e) => {
    e.preventDefault();
    if (!formDisp.medico_id) { toast.error("Selecione um médico."); return; }
    setSaving(true);
    try {
      await criarDisponibilidade({ ...formDisp, dia_semana: Number(formDisp.dia_semana) });
      toast.success("Bloco de horário criado.");
      setOpenDisp(false);
      carregar();
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(Array.isArray(det) ? det[0]?.msg : det || "Erro.");
    } finally { setSaving(false); }
  };

  const submitBloq = async (e) => {
    e.preventDefault();
    if (!formBloq.data_inicio || !formBloq.data_fim || !formBloq.motivo) { toast.error("Preencha todos os campos."); return; }
    setSaving(true);
    try {
      const payload = {
        ...formBloq,
        medico_id: formBloq.medico_id || null,
        data_inicio: new Date(formBloq.data_inicio).toISOString(),
        data_fim: new Date(formBloq.data_fim).toISOString(),
      };
      const r = await criarBloqueio(payload);
      setBloqResult(r);
      toast.success("Bloqueio criado.");
      if (r.afetadas.length === 0) {
        setOpenBloq(false);
        setBloqResult(null);
      }
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro.");
    } finally { setSaving(false); }
  };

  const notificar = async (bid) => {
    try {
      const r = await notificarBloqueio(bid);
      toast.success(`${r.notificados} tutor(es) notificado(s).`);
      setBloqResult(null);
      setOpenBloq(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro.");
    }
  };

  return (
    <div data-testid="admin-disponibilidade">
      <PageTitle subtitle="Horários por médico e bloqueios de agenda.">Disponibilidade</PageTitle>

      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold">Horários semanais</h2>
          {canEdit && <button onClick={() => setOpenDisp(true)} data-testid="btn-novo-bloco" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-4 py-2 text-sm font-bold hover:bg-[#174E5B]"><Plus className="h-3.5 w-3.5" /> Novo bloco</button>}
        </div>
        {loading ? <p className="text-[#5A5A5A]">A carregar...</p> : medicos.length === 0 ? (
          <Card><p className="text-sm text-[#5A5A5A]">Sem médicos. Crie em <a href="/admin/medicos" className="text-[#1E5F6E] font-bold underline">Médicos</a>.</p></Card>
        ) : (
          <div className="space-y-4">
            {medicos.map((m) => {
              const blocos = dispsPorMedico(m.id);
              return (
                <Card key={m.id} testId={`disp-medico-${m.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div><div className="font-display font-bold">{m.nome}</div><div className="text-xs text-[#5A5A5A]"><ClinicaLabel slug={m.clinica_principal_slug} /></div></div>
                  </div>
                  {blocos.length === 0 ? <p className="text-sm text-[#5A5A5A]">Sem horários configurados.</p> : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                      {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                        <div key={d} className="rounded-xl bg-[#FAF7F2] border border-[#1E5F6E]/10 p-2">
                          <div className="text-[10px] uppercase tracking-widest font-bold text-[#88A096]">{DIAS[d]}</div>
                          {blocos.filter((b) => b.dia_semana === d).length === 0 && <div className="text-xs text-[#5A5A5A] mt-1">Fechado</div>}
                          {blocos.filter((b) => b.dia_semana === d).map((b) => (
                            <div key={b.id} className="mt-1 flex items-center justify-between gap-1 text-xs font-semibold">
                              <span>{b.hora_inicio}–{b.hora_fim}</span>
                              {canEdit && <button onClick={() => apagarDisponibilidade(b.id).then(carregar)} data-testid={`disp-del-${b.id}`} aria-label="Remover" className="text-rose-600 hover:text-rose-800"><Trash2 className="h-3 w-3" /></button>}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold">Bloqueios de agenda</h2>
          {canEdit && <button onClick={() => { setBloqResult(null); setOpenBloq(true); }} data-testid="btn-novo-bloqueio" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-4 py-2 text-sm font-bold hover:bg-[#174E5B]"><Plus className="h-3.5 w-3.5" /> Novo bloqueio</button>}
        </div>
        {bloqueios.length === 0 ? <Card><p className="text-sm text-[#5A5A5A]">Sem bloqueios.</p></Card> : (
          <Card className="!p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF7F2] text-xs uppercase tracking-wider text-[#5A5A5A]">
                <tr><th className="text-left py-2 px-3">Clínica</th><th className="text-left py-2 px-3">Médico</th><th className="text-left py-2 px-3">Início</th><th className="text-left py-2 px-3">Fim</th><th className="text-left py-2 px-3">Motivo</th><th></th></tr>
              </thead>
              <tbody>
                {bloqueios.map((b) => (
                  <tr key={b.id} className="border-t border-[#1E5F6E]/10" data-testid={`bloq-${b.id}`}>
                    <td className="py-2 px-3"><ClinicaLabel slug={b.clinica_slug} /></td>
                    <td className="py-2 px-3 text-xs">{b.medico_id ? (medicos.find((m) => m.id === b.medico_id)?.nome || "—") : "Clínica toda"}</td>
                    <td className="py-2 px-3">{formatDate(b.data_inicio)}</td>
                    <td className="py-2 px-3">{formatDate(b.data_fim)}</td>
                    <td className="py-2 px-3">{b.motivo}</td>
                    <td className="py-2 px-3 flex gap-2 justify-end">
                      <button onClick={() => notificar(b.id)} data-testid={`bloq-notif-${b.id}`} className="text-xs rounded-full bg-[#1E5F6E] text-white px-2.5 py-1 font-bold" title="Notificar tutores afetados"><Send className="h-3 w-3 inline" /> Notificar</button>
                      {canEdit && <button onClick={() => apagarBloqueio(b.id).then(carregar)} data-testid={`bloq-del-${b.id}`} className="text-xs rounded-full bg-white border border-rose-200 text-rose-700 px-2.5 py-1 font-bold"><Trash2 className="h-3 w-3 inline" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>

      {/* Modal disponibilidade */}
      {openDisp && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setOpenDisp(false)} data-testid="disp-modal">
          <form onSubmit={submitDisp} className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg">Novo bloco de horário</h3>
              <button type="button" onClick={() => setOpenDisp(false)} className="h-8 w-8 grid place-items-center rounded-lg bg-[#FAF7F2]"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <label className="block"><span className="text-sm font-semibold">Médico *</span>
                <select required data-testid="disp-medico" value={formDisp.medico_id} onChange={(e) => setFormDisp({ ...formDisp, medico_id: e.target.value })} className={inputCls + " mt-1"}>
                  <option value="">— Selecione —</option>
                  {medicos.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
                </select></label>
              <label className="block"><span className="text-sm font-semibold">Clínica</span>
                <select data-testid="disp-clinica" value={formDisp.clinica_slug} onChange={(e) => setFormDisp({ ...formDisp, clinica_slug: e.target.value })} className={inputCls + " mt-1"}>
                  <option value="ribeira-brava">Ribeira Brava</option>
                  <option value="calheta">Calheta</option>
                </select></label>
              <label className="block"><span className="text-sm font-semibold">Dia da semana</span>
                <select data-testid="disp-dia" value={formDisp.dia_semana} onChange={(e) => setFormDisp({ ...formDisp, dia_semana: Number(e.target.value) })} className={inputCls + " mt-1"}>
                  {DIAS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-sm font-semibold">Hora início</span>
                  <input type="time" required data-testid="disp-ini" value={formDisp.hora_inicio} onChange={(e) => setFormDisp({ ...formDisp, hora_inicio: e.target.value })} className={inputCls + " mt-1"} /></label>
                <label className="block"><span className="text-sm font-semibold">Hora fim</span>
                  <input type="time" required data-testid="disp-fim" value={formDisp.hora_fim} onChange={(e) => setFormDisp({ ...formDisp, hora_fim: e.target.value })} className={inputCls + " mt-1"} /></label>
              </div>
              <label className="block"><span className="text-sm font-semibold">Slot (min)</span>
                <input type="number" min="5" max="120" data-testid="disp-slot" value={formDisp.intervalo_slot_minutos} onChange={(e) => setFormDisp({ ...formDisp, intervalo_slot_minutos: Number(e.target.value) })} className={inputCls + " mt-1"} /></label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpenDisp(false)} className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-4 py-2 text-sm font-bold text-[#1E5F6E]">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="disp-submit" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-4 py-2 text-sm font-bold disabled:opacity-40">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Criar</button>
            </div>
          </form>
        </div>
      )}

      {/* Modal bloqueio */}
      {openBloq && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => { if (!bloqResult) setOpenBloq(false); }} data-testid="bloq-modal">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {!bloqResult ? (
              <form onSubmit={submitBloq}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-bold text-lg">Novo bloqueio</h3>
                  <button type="button" onClick={() => setOpenBloq(false)} className="h-8 w-8 grid place-items-center rounded-lg bg-[#FAF7F2]"><X className="h-4 w-4" /></button>
                </div>
                <div className="space-y-3">
                  <label className="block"><span className="text-sm font-semibold">Médico (opcional — vazio = clínica toda)</span>
                    <select data-testid="bloq-medico" value={formBloq.medico_id} onChange={(e) => setFormBloq({ ...formBloq, medico_id: e.target.value })} className={inputCls + " mt-1"}>
                      <option value="">Clínica toda</option>
                      {medicos.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
                    </select></label>
                  <label className="block"><span className="text-sm font-semibold">Clínica</span>
                    <select data-testid="bloq-clinica" value={formBloq.clinica_slug} onChange={(e) => setFormBloq({ ...formBloq, clinica_slug: e.target.value })} className={inputCls + " mt-1"}>
                      <option value="ribeira-brava">Ribeira Brava</option>
                      <option value="calheta">Calheta</option>
                    </select></label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block"><span className="text-sm font-semibold">Início</span>
                      <input type="datetime-local" required data-testid="bloq-ini" value={formBloq.data_inicio} onChange={(e) => setFormBloq({ ...formBloq, data_inicio: e.target.value })} className={inputCls + " mt-1"} /></label>
                    <label className="block"><span className="text-sm font-semibold">Fim</span>
                      <input type="datetime-local" required data-testid="bloq-fim" value={formBloq.data_fim} onChange={(e) => setFormBloq({ ...formBloq, data_fim: e.target.value })} className={inputCls + " mt-1"} /></label>
                  </div>
                  <label className="block"><span className="text-sm font-semibold">Motivo</span>
                    <input required data-testid="bloq-motivo" value={formBloq.motivo} onChange={(e) => setFormBloq({ ...formBloq, motivo: e.target.value })} className={inputCls + " mt-1"} placeholder="Férias, Formação..." /></label>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button type="button" onClick={() => setOpenBloq(false)} className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-4 py-2 text-sm font-bold text-[#1E5F6E]">Cancelar</button>
                  <button type="submit" disabled={saving} data-testid="bloq-submit" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-4 py-2 text-sm font-bold disabled:opacity-40">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Criar</button>
                </div>
              </form>
            ) : (
              <div data-testid="bloq-afetadas-modal">
                <div className="flex items-start gap-3 mb-4">
                  <AlertCircle className="h-5 w-5 text-[#E8A87C] mt-0.5" />
                  <div>
                    <h3 className="font-display font-bold text-lg">{bloqResult.afetadas.length} marcação(ões) afetada(s)</h3>
                    <p className="text-sm text-[#5A5A5A]">Estas marcações ficam no período do bloqueio. Avise os tutores.</p>
                  </div>
                </div>
                <ul className="divide-y divide-[#1E5F6E]/10 text-sm max-h-[40vh] overflow-y-auto">
                  {bloqResult.afetadas.map((m) => (
                    <li key={m.id} className="py-2.5">
                      <div className="font-bold">{m.numero_marcacao} · {formatDate(m.data_hora)}</div>
                      <div className="text-xs text-[#5A5A5A]">{m.tutor.nome} ({m.tutor.email}) — {m.animal.nome}</div>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={() => { setBloqResult(null); setOpenBloq(false); }} className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-4 py-2 text-sm font-bold text-[#1E5F6E]">Mais tarde</button>
                  <button onClick={() => notificar(bloqResult.bloqueio.id)} data-testid="bloq-notif-now" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-4 py-2 text-sm font-bold"><Send className="h-3.5 w-3.5" /> Notificar tutores por email</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
